import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map } from 'rxjs/operators';
import { PendingTranslation } from '../model/PendingTranslation';
import { TranslationRecord } from '../model/TranslationRecord';
import { APP_SETTINGS } from '../../shared/models/backofficeConfiguration';
import { ExecuteService } from '../../shared/services/executeService.service';
import { ErrorHandlerService } from '../../shared/services/errorHandler.service';
import { FeedsService } from './feeds.service';
import { Feed } from '../model/Feed';
import * as moment from 'moment-timezone';
import { HttpParams, HttpErrorResponse } from '@angular/common/http';
import { PendingTranslationsFilter } from '../model/PendingTranslationsFilter';
import { AlertService } from '../../shared/alert/services/alert.service';


@Injectable()
export class PostTranslationsService {

  private apiUrl = APP_SETTINGS.TranslationBOApiUrl;

  constructor(
    private alertService: AlertService,
    private feedsService: FeedsService,
    private executeService: ExecuteService,
    private errorHandler: ErrorHandlerService) { }

  public GetPendingTranslations(
    locale: string,
    filter: PendingTranslationsFilter): Observable<PendingTranslation[]> {
    const url = `${this.apiUrl}/${locale}/api/v1/pendingTranslations`;
    const params = this.getFilterQueryParams(filter);

    return Observable.forkJoin([
      this.feedsService.GetFeeds(),
      this.executeService.ExecuteWithToken('get', url, params)])
      .pipe(
        map(([feeds, pendingTranslations]) => this.mapPendingTranslationResponses(pendingTranslations, feeds)),
        catchError(this.errorHandler.handleError<object[]>('PostTranslationsService_GetPendingTranslations', [], true))
      );
  }

  public GetPendingBySourcePostId(
    locale: string,
    sourcePostId: string): Observable<PendingTranslation> {
    const url = `${this.apiUrl}/${locale}/api/v1/pendingTranslations/${sourcePostId}`;

    return Observable.forkJoin([
      this.feedsService.GetFeeds(),
      this.executeService.ExecuteWithToken('get', url)])
      .pipe(
        map(([feeds, pendingTranslations]) => this.mapPendingTranslation(pendingTranslations, feeds)),
        catchError(this.errorHandler.handleError<PendingTranslation>('PostTranslationsService_GetPendingTranslationBySourcePostId', null, true))
      );
  }

  public CreateTranslationRecord(locale: string, sourcePostId: string, translationRecord: TranslationRecord) {
    const url = `${this.apiUrl}/${locale}/api/v1/translations/${sourcePostId}`;

    return this.executeService.ExecuteWithToken('post', url, null, translationRecord)
      .pipe(
        catchError((error: HttpErrorResponse): any => {
          if (error.status === 400) {
            this.alertService.add('danger',
              'ERROR: The source post does not have any pending translation for this language. It may have already been translated.');
            return Observable.throw(error);
          }
          return this.errorHandler.handleAndThrowError('PostTranslationsService_CreateTranslationRecord', null, true);
        }));
  }

  public UpdateDraftId(locale: string, sourcePostId: string, draftId: string, languageId: string) {
    const url = `${this.apiUrl}/${locale}/api/v1/pendingTranslations/${sourcePostId}/draft`;
    let params = new HttpParams();
    params = params.set('draftId', draftId);
    params = params.set('languageId', languageId);

    return this.executeService.ExecuteWithToken('put', url, params)
      .pipe(
        catchError((error: HttpErrorResponse): any => {
          return this.errorHandler.handleAndThrowError('PostTranslationsService_UpdateDraftId', null, true);
        }));
  }

  public TranslatePost(locale: string, sourcePostId: string, title: string, content: string): Observable<PendingTranslation> {

    const url = `${this.apiUrl}/${locale}/api/v1/PendingTranslations/${sourcePostId}/translate`;

    return this.executeService.ExecuteWithToken('post', url, null, {sourceTitle: title, sourceContent: content })
      .pipe(catchError((error: HttpErrorResponse) => Observable.throw(error)));
  }

  private getFilterQueryParams(filter: PendingTranslationsFilter): HttpParams {
    let params = new HttpParams;

    if (filter.dateFrom) {
      params = params.set('dateFrom', filter.dateFrom.toISOString());
    }
    if (filter.dateTo) {
      params = params.set('dateTo', filter.dateTo.toISOString());
    }
    if (filter.feedId) {
      params = params.set('feedId', filter.feedId);
    }
    if (filter.isHighlighted) {
      params = params.set('isHighlighted', filter.isHighlighted.toString());
    }
    if (filter.sourceTitle) {
      params = params.set('sourceTitle', filter.sourceTitle);
    } 
    if (filter.authorId) {
      params = params.set('authorId', filter.authorId);
    }   

    return params;
  }

  private mapPendingTranslationResponses(pendingTranslations: any[], feeds: any[]): any {
    
    if (pendingTranslations == null || feeds == null)
      return [];

    const result: PendingTranslation[] = pendingTranslations.map(pt => {
      var mapped = this.mapPendingTranslation(pt, feeds);
      return mapped;
    });

    return result;
  }

  private mapPendingTranslation(pt: any, feeds: Array<Feed>): PendingTranslation {

    const feed = feeds.find(f => f.id === pt.feedId);
    const feedName = (feed && feed.name) || '?';

    const result = {
      sourcePostId: pt.sourcePostId,
      feedName: feedName,
      feedId: pt.feedId,
      sourceTitle: pt.sourceTitle,
      isHighlighted: pt.isHighlighted,
      sourceWordCount: pt.sourceWordCount,
      publicationDateUtc: moment(pt.publicationDateUtc),
      draftId: pt.draftId,
      translationSchedulingStatus: pt.translationSchedulingStatus,
      automaticContentTranslation: pt.automaticContentTranslation,
      automaticTitleTranslation: pt.automaticTitleTranslation,
      translationSchedulingDateUtc: pt.translationSchedulingDateUtc,
      pendingTranslationStatus: pt.pendingTranslationStatus
    };

    return result;
  }
}



// WEBPACK FOOTER //
// ./src/app/postTranslations/services/postTranslations.service.ts